import {ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {
  DownloadableFile,
  FindTransitSummaries,
  InitiateTransitForForwarder,
  Item,
  TransitDateFilterType,
  TransitDeclaration,
  TransitInspectionStatus,
  TransitPhase,
  TransitSortOrder,
  TransitSortType,
  TransitSummariesResult,
  TransitSummary
} from '@portbase/bezoekschip-service-typescriptmodels';
import {AppContext} from '../app-context';
import {cloneObject, lodash, nanoId, removeItem, sendQuery, toWebsocketUrl} from '../common/utils';
import {AbstractOverviewComponent} from '../common/abstract-overview.component';
import {Observable, of, Subject} from 'rxjs';
import {createEmptySummary, initializeTransit, loadTransit, Transit} from './transit.utils';
import {concatMap, map, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {TransitFilter, transitPhases} from './transit-filter/transit-filter.component';
import moment, {Moment} from 'moment/moment';
import {PortvisitUtils} from '../refdata/portvisit-utils';
import {ActivatedRoute} from '@angular/router';
import {HttpClient} from "@angular/common/http";
import {downloadAny} from "../common/download/download.utils";
import {DateRange} from "../common/date/date-range/date-range";

@Component({
  selector: 'app-transit-overview',
  templateUrl: './transit-overview.component.html',
  styleUrls: ['./transit-overview.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TransitOverviewComponent extends AbstractOverviewComponent<Transit> implements OnInit, OnDestroy {
  appContext = AppContext;
  refData = PortvisitUtils;
  transits: Transit[] = [];
  socket: WebSocket;
  transitFilter: TransitFilter = {phaseCounts: {}, deselectedOptions: [], selectedOptions: [], ready: false};
  sortBy = 'sendDateDescending';
  showFilter: boolean = true;
  transitDateFilterType: TransitDateFilterType = TransitDateFilterType.TRANSPORT_TERM;
  transitDateFilterOptions: string[] = [TransitDateFilterType.TRANSPORT_TERM,
    TransitDateFilterType.SENT_DATE, TransitDateFilterType.TRANSPORT_EXPIRY_DATE];
  preselectedItems: Item[] = [];
  sortingProperties = ['sendDate', 'sendDateDescending', 'containerNumber', 'containerNumberDescending'];
  transitDateFilterTypeFormatter = (option) => {
    switch (option) {
      case TransitDateFilterType.TRANSPORT_TERM:
        return "Transport term";
      case TransitDateFilterType.SENT_DATE:
        return "Sent date";
      case TransitDateFilterType.TRANSPORT_EXPIRY_DATE:
        return "Transport expiry date";
    }
  };

  // Server-side pagination state
  page: number = 1;
  pageSize: number = 20;
  totalItems: number = 0;

  // Client-side, unsaved declarations that are shown at the top of the first page.
  newDeclarations: Transit[] = [];

  // Individually selected declarations, tracked by LRN so the selection survives navigation
  // between (server-loaded) pages. Only used while "Select all" is NOT active.
  selectedLrns: Set<string> = new Set<string>();

  // True when every declaration matching the current filters is selected (across all pages), not
  // just the loaded page. This is only a flag: the actual LRNs are resolved lazily (via
  // FindTransitLrns) when a bulk action runs, so selecting all does not hit the backend.
  allSelected: boolean = false;

  // While "Select all" is active, declarations the user manually deselects are tracked here and
  // excluded from bulk actions, so "all except a few" keeps working without materialising the set.
  deselectedLrns: Set<string> = new Set<string>();

  private documentsDownloader: Subject<DownloadableFile> = new Subject<DownloadableFile>();

  localStorageKey(): string {
    return "transit-overview";
  }

  constructor(private http: HttpClient, private zone: NgZone, route: ActivatedRoute) {
    super();

    route.queryParams.subscribe(queryParams => this.initializeForIFrame(
      queryParams['crn'],
      queryParams['consignmentNumber'],
      queryParams['containerNumber'],
      queryParams['items']));
  }

  private initializeForIFrame(crn, consignmentNumber, containerNumber, items) {
    if (crn && consignmentNumber) {
      this.preselectedItems = [{
        crn: crn,
        consignmentNumber: consignmentNumber,
        containerNumber: containerNumber
      }];
    } else if (items) {
      const initiateTransit: InitiateTransitForForwarder = JSON.parse(atob(items));

      this.preselectedItems = initiateTransit.items;
    }

    if (this.preselectedItems.length > 0) {
      setTimeout(() => $('#preselectedDeclaration').modal('show'), 0);
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subscribeToTransitUpdates();
    this.subscribeToDownloadedFiles();
  }

  createNextTransitDeclaration = (previous: Transit) => {
    const next = cloneObject(previous);
    const lrn = nanoId();

    delete next.declaration.crn;
    delete next.declaration.status;
    delete next.declaration.dataFromCustoms;
    delete next.declaration.requestedTransportExpiryDate;
    delete next.declaration.transportExpiryDate;
    delete next.summary.crn;
    delete next.summary.mrn;
    delete next.summary.actualDestinationCode;
    delete next.summary.clearanceCode;
    delete next.summary.investigatingOffice;
    delete next.summary.mrnSentDate;
    delete next.summary.mrnAcceptDate;
    delete next.summary.mrnRejectDate;
    delete next.summary.mrnRejectReason;
    delete next.summary.infoRequestExpirationDate;
    delete next.summary.sentDate;
    delete next.summary.transportExpiryDate;

    next.declaration.lrn = lrn;
    next.summary.lrn = lrn;
    next.summary.sendDate = moment().format('YYYY-MM-DD');
    next.summary.inspectionStatus = <TransitInspectionStatus>{};
    next.summary.cancelInitiatedByCustoms = false;
    next.summary.isInfoDeclared = false;
    next.summary.isReleased = false;
    next.summary.sendOnAta = false;
    next.summary.phase = TransitPhase.undeclared;
    next.declaration.data.goodsItems.forEach(g => g.previousDocument = {
      type: {
        "code": "N821",
        "description": "Aangifte tot plaatsing onder de regeling extern communautair douanevervoer/gemeenschappelijk douanevervoer, T1"
      },
      documentNumber: previous.declaration.status.mrn
    });

    this.addDeclaration(initializeTransit(next.declaration), next.summary, !!next.declaration.data.containerNumber);
  }

  copyTransitDeclaration = (previous: Transit) => {
    const next = cloneObject(previous);
    const lrn = nanoId();

    delete next.declaration.status;
    delete next.declaration.dataFromCustoms;
    delete next.declaration.requestedTransportExpiryDate;
    delete next.declaration.transportExpiryDate;
    delete next.summary.mrn;
    delete next.summary.actualDestinationCode;
    delete next.summary.clearanceCode;
    delete next.summary.investigatingOffice;
    delete next.summary.mrnSentDate;
    delete next.summary.mrnAcceptDate;
    delete next.summary.mrnRejectDate;
    delete next.summary.mrnRejectReason;
    delete next.summary.infoRequestExpirationDate;
    delete next.summary.sentDate;
    delete next.summary.transportExpiryDate;

    next.declaration.lrn = lrn;
    next.summary.lrn = lrn;
    next.summary.sendDate = moment().format('YYYY-MM-DD');
    next.summary.inspectionStatus = <TransitInspectionStatus>{};
    next.summary.cancelInitiatedByCustoms = false;
    next.summary.isInfoDeclared = false;
    next.summary.isReleased = false;
    next.summary.sendOnAta = false;
    next.summary.phase = TransitPhase.undeclared;

    this.addDeclaration(initializeTransit(next.declaration), next.summary, !!next.declaration.data.containerNumber);
  }

  get visibleDeclarations(): Transit[] {
    return this.transits.filter(s => !s.hidden);
  }

  // True when the server reports any declarations in the current date range (across all phases).
  get hasDeclarationsInRange(): boolean {
    const counts = this.transitFilter.phaseCounts || {};
    return Object.keys(counts).some(phase => counts[phase] > 0);
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
  }

  // The server already applies the term filter, so we never filter client-side.
  getFilteredItems = (): Transit[] => this.items;

  // Reused by app-filter's [searchFunction]. A new search must go back to the first page.
  reload = (): Observable<Transit[]> => {
    this.page = 1;
    return this.loadCurrentPage().pipe(tap(entries => this.items = entries));
  };

  // Changing the date range must reset to the first page before (re)loading server-side.
  onDateSelection = (dateRange: DateRange): void => {
    if (dateRange) {
      const now = moment().startOf('day');
      const daysBefore = now.diff(moment(dateRange.start), 'days');
      const daysAfter = moment(dateRange.end).diff(now, 'days');
      this.dateRange = dateRange;
      this.from = 0;
      this.endReached = false;
      this.page = 1;
      this.resetSelection();
      this.loadAndRender();

      if (this.localStorageKey()) {
        localStorage.setItem(this.localStorageKey(), JSON.stringify({
          daysBefore: daysBefore,
          daysAfter: daysAfter
        }));
      }
    }
  };

  doLoad(start: Moment, end: Moment): Observable<(Transit)[]> {
    return this.loadCurrentPage();
  }

  // Shared term/date/phase filter and sort params, used by both the paginated summary query and
  // the "Select all" LRN query, so both operate on exactly the same filtered set in the same order.
  private getTransitFilterParams() {
    const start = moment(this.dateRange.start);
    const end = moment(this.dateRange.end).add(1, 'days');
    const phases = (this.transitFilter.selectedOptions.length > 0 ? this.transitFilter.selectedOptions : transitPhases)
      .filter(option => !this.transitFilter.deselectedOptions.some(deselectedOption => option === deselectedOption));
    return {
      term: this.filterTerm,
      declarantShortName: AppContext.isAdmin() ? null : AppContext.userProfile.organisation?.shortName,
      start: start.toISOString(),
      end: end.toISOString(),
      filterType: this.transitDateFilterType,
      phases: phases,
      ...this.getSortParams(),
    };
  }

  private loadCurrentPage(): Observable<Transit[]> {
    if (!this.transitFilter.ready) {
      return of([]);
    }

    return sendQuery('com.portbase.bezoekschip.common.api.transit.FindTransitSummaries',
      <FindTransitSummaries>{
        ...this.getTransitFilterParams(),
        from: (this.page - 1) * this.pageSize,
        maxHits: this.pageSize,
      }, {caching: false, showSpinner: true})
      .pipe(map((result: TransitSummariesResult) => {
        return this.zone.runOutsideAngular(() => {
          const transitSummaries = result?.transitSummaries;
          const phaseCounts = result.phaseCounts;

          this.transitFilter.phaseCounts = phaseCounts;

          // The result has no total count, so derive it from the per-phase counts. Only include
          // the phases that are actually being queried: those not deselected and, when a selection
          // is active, only the selected ones. That sum equals the size of the (term + date + phase)
          // filtered set that is being paginated server-side.
          const selectedOptions = this.transitFilter.selectedOptions;
          const deselectedOptions = this.transitFilter.deselectedOptions;
          this.totalItems = (Object.keys(phaseCounts) as TransitPhase[])
            .filter(phase => !deselectedOptions.includes(phase)
              && (selectedOptions.length === 0 || selectedOptions.includes(phase)))
            .reduce((total, phase) => total + phaseCounts[phase], 0);

          if (!transitSummaries) {
            return this.items;
          } else {
            return transitSummaries.map(summary => <Transit>{
              summary: summary, hidden: false, isContainerDeclaration: !!summary.containerNumber,
              isNewTransitDeclaration: false, selected: this.isSelected(summary.lrn)
            });
          }
        });
      }));
  }

  goToPage = (page: number) => {
    this.page = page;
    this.loadAndRender();
  };

  doRender = (transits: Transit[]) => {
    // Unsaved declarations live client-side and are shown on top of the first page.
    const displayed = (this.page === 1 ? this.newDeclarations : []).concat(transits);
    displayed.forEach(t => {
      // Unsaved declarations are never covered by "Select all"; they must be picked individually.
      t.selected = !t.isNewTransitDeclaration && this.isSelected(t.summary.lrn);
    });
    this.transits = displayed;
  };

  addNewContainerDeclaration = () => {
    const transitDeclaration: TransitDeclaration = initializeTransit();
    this.addDeclaration(transitDeclaration, createEmptySummary(transitDeclaration.lrn), true);
  };

  addNewBulkDeclaration = () => {
    const transitDeclaration: TransitDeclaration = initializeTransit();
    this.addDeclaration(transitDeclaration, createEmptySummary(transitDeclaration.lrn), false);
  };

  addDeclaration = (declaration: TransitDeclaration, summary: TransitSummary, isContainerDeclaration: boolean) => {
    this.newDeclarations.splice(0, 0, <Transit>{
      summary: summary, hidden: false, isContainerDeclaration: isContainerDeclaration, isNewTransitDeclaration: true,
      selected: false, declaration: initializeTransit(declaration)
    });
    if (this.page === 1) {
      this.renderFilteredItems();
    } else {
      this.page = 1;
      this.loadAndRender();
    }
  };

  handleStatusUpdate = (summary: TransitSummary) => {
    const existing = this.newDeclarations.concat(this.items).find(i => i.summary.lrn === summary.lrn);

    if (existing) {
      const transit = <Transit>{
        summary: summary, hidden: existing.hidden, isContainerDeclaration: !!summary.containerNumber,
        isNewTransitDeclaration: false, selected: existing.selected, declaration: null
      };

      if (!existing.declaration) {
        lodash.assign(existing, transit);
      } else {
        loadTransit(transit).then(() => lodash.assign(existing, transit));
      }
    }
  };

  applyTransitFilters(transitFilter: TransitFilter) {
    this.transitFilter = transitFilter;
    this.page = 1;
    this.resetSelection();
    this.loadAndRender();
  }

  private subscribeToTransitUpdates = () => {
    if (environment.production || environment.docker) {
      try {
        this.socket = new WebSocket(toWebsocketUrl('/api/ui/transit-declarations'));
      } catch (e) {
        console.info('Could not open websocket. Retrying every minute...', e);
        setTimeout(this.subscribeToTransitUpdates, 60_000);
        return;
      }
      this.socket.onmessage = (message: MessageEvent) => {
        if (typeof message.data === 'string') {
          this.handleStatusUpdate(JSON.parse(message.data));
        }
      };
      this.socket.onclose = (event: CloseEvent) => {
        if (!event.wasClean) {
          console.warn('Websocket closed with reason: ' + event.reason + ' (' + event.code + '). Trying to reconnect...');
        }
        setTimeout(() => this.subscribeToTransitUpdates(), 5_000);
      };
    }
  };

  onDateTypeChanged(option): void {
    this.resetSelection();
    this.loadAndRender();
  }

  // Used as the search box's searchFunction: a search term changes the matching set, so drop any
  // active selection before reloading.
  searchTransit = (): Observable<Transit[]> => {
    this.resetSelection();
    return this.reload();
  };

  removeNewDeclaration(item: Transit) {
    return () => {
      removeItem(this.newDeclarations, item);
      this.renderFilteredItems();
    };
  }

  changeSorting(sortBy: string) {
    this.sortBy = sortBy;
    this.page = 1;
    this.loadAndRender();
  }

  protected sortingFormatter = (sortingProperty: string): string => {
    switch (sortingProperty) {
      case "sendDate":
        return `Send date (Ascending)`;
      case "sendDateDescending":
        return `Send date (Descending)`;
      case "containerNumber":
        return `Container number (Ascending)`;
      case "containerNumberDescending":
        return `Container number (Descending)`;
    }
  }

  private getSortParams(): { sortType: TransitSortType, sortOrder: TransitSortOrder } {
    switch (this.sortBy) {
      case 'containerNumber':
        return {sortType: TransitSortType.CONTAINER_NUMBER, sortOrder: TransitSortOrder.ASCENDING};
      case 'containerNumberDescending':
        return {sortType: TransitSortType.CONTAINER_NUMBER, sortOrder: TransitSortOrder.DESCENDING};
      case 'sendDate':
        return {sortType: TransitSortType.SEND_DATE, sortOrder: TransitSortOrder.ASCENDING};
      default:
        return {sortType: TransitSortType.SEND_DATE, sortOrder: TransitSortOrder.DESCENDING};
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.transits.forEach(t => {
      t.selected = false;
    });
    this.selectedLrns.clear();
    this.deselectedLrns.clear();
    this.allSelected = false;
    try {
      this.socket.onclose = () => {
      };
      this.socket.close();
      this.socket = null;
    } catch (ignored) {
    }
  }

  /*
    Bulk actions
   */

  get selectedItems(): Transit[] {
    return this.transits.filter(s => s.selected);
  }

  get selectedCount(): number {
    // While "Select all" is active the set isn't materialised, so derive the count from the total
    // matching the current filters minus any manually deselected declarations.
    return this.allSelected ? Math.max(this.totalItems - this.deselectedLrns.size, 0) : this.selectedLrns.size;
  }

  // Whether the given LRN is currently selected, taking "Select all" and its exclusions into account.
  private isSelected(lrn: string): boolean {
    return this.allSelected ? !this.deselectedLrns.has(lrn) : this.selectedLrns.has(lrn);
  }

  setSelected(declaration: Transit, selected: boolean) {
    declaration.selected = selected;
    const lrn = declaration.summary.lrn;
    if (this.allSelected) {
      // "Select all" is active, so track individual changes as exclusions from the whole set.
      if (selected) {
        this.deselectedLrns.delete(lrn);
      } else {
        this.deselectedLrns.add(lrn);
      }
    } else if (selected) {
      this.selectedLrns.add(lrn);
    } else {
      this.selectedLrns.delete(lrn);
    }
  }

  toggleSelectAll = () => {
    if (this.allSelected) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  };

  // Marks every declaration matching the current filters as selected. This only sets a flag; the
  // actual LRNs are resolved lazily (see resolveSelectedLrns) when a bulk action runs, so no
  // backend query is issued here.
  private selectAll = () => {
    this.allSelected = true;
    this.selectedLrns.clear();
    this.deselectedLrns.clear();
    this.visibleDeclarations.forEach(d => d.selected = !d.isNewTransitDeclaration);
  };

  private deselectAll = () => {
    this.resetSelection();
    this.visibleDeclarations.forEach(d => d.selected = false);
  };

  // Clears all selection state. Called whenever the filtered set changes (date range, filters,
  // date filter type or search term), so "Select all" never spans a set the user can no longer see.
  private resetSelection = () => {
    this.selectedLrns.clear();
    this.deselectedLrns.clear();
    this.allSelected = false;
  };

  // Resolves the LRNs a bulk action should apply to. When "Select all" is active the matching LRNs
  // are fetched from the backend on demand (minus any manually deselected ones); otherwise the
  // individually selected LRNs are used.
  private resolveSelectedLrns = (): Observable<string[]> => {
    if (!this.allSelected) {
      return of(Array.from(this.selectedLrns));
    }
    return AppContext.waitForProcess(
      sendQuery('com.portbase.bezoekschip.common.api.transit.FindTransitLrns',
        this.getTransitFilterParams(), {caching: false, showSpinner: true}))
      .pipe(map((lrns: string[]) => lrns.filter(lrn => !this.deselectedLrns.has(lrn))));
  };

  printSelected = (original: boolean) => {
    this.resolveSelectedLrns().subscribe((lrns: string[]) => {
      lrns.forEach(lrn => {
        sendQuery('com.portbase.bezoekschip.common.api.transit.GetTransitAccompanyingDocument',
          {lrn: lrn, original: original}, {caching: false, showSpinner: true})
          .subscribe((file: DownloadableFile) => {
            this.documentsDownloader.next(file);
          });
      });
    });
  };

  subscribeToDownloadedFiles = () => {
    this.documentsDownloader
      .pipe(concatMap(file => this.downloadFile(file)))
      .subscribe({
        next(file: DownloadableFile) {
        }
      });
  }

  private downloadFile(file: DownloadableFile) {
    if (!file) {
      return Promise.resolve(null);
    } else {
      const a = document.createElement('a');
      a.href = `data:${(file.mediaType)};base64,${(file.data)}`;
      a.setAttribute('download', file.fileName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return new Promise(resolve => setTimeout(() => resolve(file), 100));
    }
  }

  downloadCsv() {
    this.resolveSelectedLrns().subscribe((lrns: string[]) => {
      AppContext.waitForProcess(this.http.post("/api/transit/csv", lrns, {responseType: 'text'})
        .pipe(tap({error: e => AppContext.registerError(e)})))
        .subscribe(content => downloadAny(content, "transit.csv", "text/plain"));
    });
  }

  calculateTransportExpiryDate(date: string, transportTerm: string): string {
    return moment(date).add(transportTerm, 'seconds').format("DD-MM-YYYY");
  }

  transportTermToDays(transportTerm: string): number {
    return moment.duration(transportTerm, 'seconds').asDays();
  }

  determineStatusTooltip(summary: TransitSummary): string {
    if (summary.sentDate) {
      return "date sent / transport expiry date";
    } else if (summary.sendOnAta) {
      return "on ATA / transport term";
    } else {
      return "send date / transport expiry date";
    }
  }
}
